import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { Eye, EyeOff } from 'lucide-react';
import { Badge, Input, getStatusVariant, type BadgeVariant } from '@/components/ui';
import { useSpaceId } from '@/contexts/SpaceContext';
import { routes } from '@/lib/routes';
import type { Plugin, PluginStatus } from '@/types';
import { PluginGridCard, TypeIcon } from './PluginGridCard';

interface Props {
  plugins: Plugin[];
  isLoading: boolean;
}

const STATUS_CHIPS: { label: string; value: PluginStatus }[] = [
  { label: 'Active', value: 'active' },
  { label: 'Syncing', value: 'syncing' },
  { label: 'Failed', value: 'failed' },
];

function pluginStatusVariant(status: PluginStatus): BadgeVariant {
  if (status === 'syncing') return 'info';
  return getStatusVariant(status);
}

/**
 * Installed-plugin grid. Mirrors the catalog card layout so the page reads
 * as one continuous surface, with the per-card action replaced by a status
 * badge and a click-to-detail navigation.
 */
export default function InstalledPluginsList({ plugins, isLoading }: Props) {
  const navigate = useNavigate();
  const spaceId = useSpaceId();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [enabledStatuses, setEnabledStatuses] = useState<Set<PluginStatus>>(
    new Set(['active', 'syncing', 'failed']),
  );

  // `Esc` focuses the plugin search. Routed via react-hotkeys-hook so it shows
  // up in the Shift+? cheatsheet and inherits its built-in skip for
  // input/textarea/contentEditable targets.
  useHotkeys(
    'escape',
    (event) => {
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    {
      description: 'Focus plugin search',
      metadata: { group: 'Plugins' },
    },
  );

  const filtered = plugins.filter((p) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      p.name.toLowerCase().includes(q) ||
      (p.displayName ?? '').toLowerCase().includes(q) ||
      (p.description ?? '').toLowerCase().includes(q) ||
      p.source.repoUrl.toLowerCase().includes(q);
    return matchesSearch && enabledStatuses.has(p.status);
  });

  function toggleStatus(status: PluginStatus) {
    setEnabledStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[12rem] max-w-md">
          <Input
            ref={searchInputRef}
            placeholder="Search plugins..."
            value={searchQuery}
            onChange={(e: { target: { value: string } }) =>
              setSearchQuery(e.target.value)
            }
          />
        </div>
        {STATUS_CHIPS.map((chip) => {
          const enabled = enabledStatuses.has(chip.value);
          return (
            <button
              key={chip.value}
              type="button"
              onClick={() => toggleStatus(chip.value)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                enabled
                  ? 'bg-background text-foreground border-border hover:bg-accent'
                  : 'bg-muted text-muted-foreground border-muted line-through'
              }`}
            >
              {enabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {chip.label}
            </button>
          );
        })}
      </div>

      {isLoading && plugins.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-36 rounded-xl border border-border bg-card animate-pulse"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No plugins match the current filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((plugin) => (
            <InstalledCard
              key={plugin.pluginId}
              plugin={plugin}
              onOpen={() =>
                navigate(routes.console.spacePlugin(spaceId, plugin.pluginId))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CardProps {
  plugin: Plugin;
  onOpen: () => void;
}

function InstalledCard({ plugin, onOpen }: CardProps) {
  const metaText = [
    plugin.version ? `v${plugin.version}` : undefined,
    plugin.author?.name?.trim() ? `by ${plugin.author.name.trim()}` : undefined,
    `${plugin.source.ref} (${plugin.source.refType})`,
  ]
    .filter((p): p is string => !!p)
    .join(' · ');

  const titleAside = (
    <div className="flex items-center gap-1.5">
      {plugin.upstream?.refMissing && (
        <Badge variant="warning">Ref missing</Badge>
      )}
      {plugin.upstream?.updateAvailable && (
        <Badge variant="info">Update</Badge>
      )}
      <Badge variant={pluginStatusVariant(plugin.status)}>
        {plugin.status}
      </Badge>
    </div>
  );

  // The Plugin entity doesn't carry the catalog's `type` discriminator yet —
  // pass undefined and TypeIcon falls back to the generic Puzzle icon.
  return (
    <PluginGridCard
      icon={<TypeIcon type={undefined} size="compact" />}
      title={plugin.displayName ?? plugin.name}
      titleAside={titleAside}
      description={plugin.description?.trim() || plugin.name}
      repoUrl={plugin.source.repoUrl}
      subdir={plugin.source.subdir}
      metaText={metaText}
      onClick={onOpen}
      size="compact"
    />
  );
}
